<template>
  <div class="cw-otp-input">
    <div class="otp-container">
      <input
        v-for="index in 6"
        :key="index"
        :ref="el => (inputRefs[index - 1] = el)"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="1"
        class="otp-slot"
        :class="{ 'is-error': hasError }"
        :value="digits[index - 1]"
        @input="handleInput(index - 1, $event)"
        @keydown="handleKeydown(index - 1, $event)"
        @paste="handlePaste"
        autocomplete="one-time-code"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'cw-otp-input',
  props: {
    value: {
      type: String,
      default: '',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      digits: ['', '', '', '', '', ''],
      inputRefs: [],
    };
  },
  watch: {
    value(newVal) {
      // Update digits when value prop changes
      this.digits = (newVal || '')
        .padEnd(6, '')
        .slice(0, 6)
        .split('');
    },
  },
  mounted() {
    // Initialize digits from value prop
    this.digits = (this.value || '')
      .padEnd(6, '')
      .slice(0, 6)
      .split('');
    // Auto-focus first input
    if (this.inputRefs[0]) {
      this.inputRefs[0].focus();
    }
  },
  methods: {
    handleInput(index, event) {
      const input = event.target.value;

      // Only allow numeric input
      if (input && !/^\d$/.test(input)) {
        event.target.value = this.digits[index];
        return;
      }

      // Update the digit at this index
      this.digits[index] = input;

      // Emit the complete code
      const code = this.digits.join('');
      this.$emit('input', code);
      this.$emit('change', code);

      // Move to next input if digit was entered
      if (input && index < 5) {
        const nextInput = this.inputRefs[index + 1];
        if (nextInput) nextInput.focus();
      }

      // Auto-submit when all 6 digits are entered
      if (code.length === 6 && !code.includes('')) {
        this.$emit('complete', code);
      }
    },

    handleKeydown(index, event) {
      // Handle backspace
      if (event.key === 'Backspace') {
        if (!this.digits[index] && index > 0) {
          // If current input is empty, move to previous and clear it
          this.digits[index - 1] = '';
          const prevInput = this.inputRefs[index - 1];
          if (prevInput) prevInput.focus();
          this.$emit('input', this.digits.join(''));
          this.$emit('change', this.digits.join(''));
        } else if (this.digits[index]) {
          // Clear current digit
          this.digits[index] = '';
          this.$emit('input', this.digits.join(''));
          this.$emit('change', this.digits.join(''));
        }
        event.preventDefault();
      }
      // Handle left arrow
      else if (event.key === 'ArrowLeft' && index > 0) {
        const prevInput = this.inputRefs[index - 1];
        if (prevInput) prevInput.focus();
      }
      // Handle right arrow
      else if (event.key === 'ArrowRight' && index < 5) {
        const nextInput = this.inputRefs[index + 1];
        if (nextInput) nextInput.focus();
      }
    },

    handlePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text').trim();

      // Only process if it's 6 digits
      if (/^\d{6}$/.test(pastedData)) {
        this.digits = pastedData.split('');
        this.$emit('input', pastedData);
        this.$emit('change', pastedData);
        this.$emit('complete', pastedData);

        // Focus last input
        const lastInput = this.inputRefs[5];
        if (lastInput) lastInput.focus();
      }
    },

    clear() {
      this.digits = ['', '', '', '', '', ''];
      this.$emit('input', '');
      this.$emit('change', '');
      if (this.inputRefs[0]) {
        this.inputRefs[0].focus();
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.cw-otp-input
  width 100%

.otp-container
  display flex
  gap 0.5rem
  justify-content center
  align-items center

.otp-slot
  width 3rem
  height 3rem
  text-align center
  font-size 1.25rem
  font-weight 500
  border 2px solid #e5e5e5
  border-radius 8px
  background-color white
  transition all 0.2s ease
  outline none

  @media screen and (min-width: 640px)
    width 3.5rem
    height 3.5rem
    font-size 1.5rem

  &:focus
    border-color #4e8793
    box-shadow 0 0 0 3px rgba(78, 135, 147, 0.1)

  &.is-error
    border-color #ff0000

    &:focus
      box-shadow 0 0 0 3px rgba(255, 0, 0, 0.1)

  // Remove number input spinners
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button
    -webkit-appearance none
    margin 0

  &[type=number]
    -moz-appearance textfield
</style>
